import { ExternalLink, TriangleAlert } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useGetConnectionHealth } from '@/features/integrations/api';

import { ConnectionHealthRefresh } from './connection-health-refresh';

/**
 * The payments half of connection health, for the payments settings page.
 *
 * Stripe is deliberately absent from the integrations dashboard — that page has
 * no Stripe card and no Stripe controls, so a Stripe problem raised there sends
 * people hunting for something that is not on the screen. This is where it can
 * actually be acted on.
 *
 * The audit that prompted all of this found eleven organisations who had
 * connected Stripe and could not take a single payment, four of them having
 * never submitted anything at all. Two were one verification document away and
 * had been for weeks.
 */
export function PaymentsHealthBanner() {
  const { problems, isLoading } = useGetConnectionHealth();

  // Silent while loading and on error — the hook defaults to healthy, so a
  // failed lookup can never render as "you cannot take payments".
  if (isLoading) return null;

  const stripe = problems.find((p) => p.provider === 'stripe_connect');
  if (!stripe) return null;

  const days = Math.max(
    0,
    Math.floor(
      (Date.now() - new Date(stripe.stateSince).getTime()) /
        (24 * 60 * 60 * 1000)
    )
  );

  return (
    <Alert variant="destructive">
      <TriangleAlert className="h-4 w-4" />
      <AlertTitle>
        {stripe.remedy === 'finish_setup'
          ? "You can't take payments yet"
          : 'Payments have stopped working'}
        {days >= 1 &&
          (days === 1 ? ' — since yesterday' : ` — for ${days} days`)}
      </AlertTitle>
      <AlertDescription className="flex flex-col gap-3">
        <span>{stripe.detail ?? 'Your Stripe account needs attention.'}</span>
        <div className="flex flex-wrap items-center gap-3">
          {stripe.actionUrl ? (
            <Button asChild variant="outline" size="sm">
              <a href={stripe.actionUrl} target="_blank" rel="noreferrer">
                {stripe.actionLabel ?? 'Resolve with Stripe'}
                <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
              </a>
            </Button>
          ) : null}
          {/* Stripe's own review can clear between sweeps, and someone who has
              just submitted a document is exactly the person staring at this. */}
          <ConnectionHealthRefresh />
        </div>
      </AlertDescription>
    </Alert>
  );
}
